<script>
  import { createEventDispatcher } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faChevronUp, faChevronDown, faTrashCan, faSquarePlus } from '@fortawesome/pro-light-svg-icons';

  const dispatch = createEventDispatcher();
  export let showForm = false;
  export let editWorkflow = null;

  let workflow = {
    name: '',
    description: '',
    version: '',
    active: true,
    required: false,
    tasks: []
  };

  let previousEditId = null;
  $: if (showForm) {
    const currentId = editWorkflow?._id || '__create__';
    if (currentId !== previousEditId) {
      if (editWorkflow) {
        workflow = {
          _id: editWorkflow._id,
          name: editWorkflow.name || '',
          description: editWorkflow.description || '',
          version: editWorkflow.version || '',
          active: editWorkflow.active !== false,
          required: !!editWorkflow.required,
          tasks: Array.isArray(editWorkflow.tasks)
            ? editWorkflow.tasks.map((t, idx) => ({
                _id: t._id,
                title: t.title || `Task ${idx + 1}`,
                s3Key: t.s3Key || '',
                description: t.description || '',
                sortOrder: typeof t.sortOrder === 'number' ? t.sortOrder : idx
              }))
            : []
        };
      } else {
        workflow = { name: '', description: '', version: '', active: true, required: false, tasks: [] };
      }
      previousEditId = currentId;
    }
  }

  function close() {
    dispatch('close');
  }

  function addTask() {
    const nextOrder = (workflow.tasks?.length || 0);
    const uid = `${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
    workflow.tasks = [
      ...(workflow.tasks || []),
      { _id: uid, title: `Task ${nextOrder + 1}`, s3Key: '', description: '', sortOrder: nextOrder }
    ];
  }

  function removeTask(index) {
    workflow.tasks = workflow.tasks.filter((_, i) => i !== index).map((t, i) => ({ ...t, sortOrder: i }));
  }

  function moveTaskUp(index) {
    if (index <= 0) return;
    const tasks = [...workflow.tasks];
    const tmp = tasks[index - 1];
    tasks[index - 1] = tasks[index];
    tasks[index] = tmp;
    workflow.tasks = tasks.map((t, i) => ({ ...t, sortOrder: i }));
  }

  function moveTaskDown(index) {
    if (index >= workflow.tasks.length - 1) return;
    const tasks = [...workflow.tasks];
    const tmp = tasks[index + 1];
    tasks[index + 1] = tasks[index];
    tasks[index] = tmp;
    workflow.tasks = tasks.map((t, i) => ({ ...t, sortOrder: i }));
  }

  function handleSubmit() {
    // Basic validation
    if (!workflow.name?.trim()) {
      alert('Name is required');
      return;
    }
    if ((workflow.tasks || []).some(t => !t.s3Key?.trim())) {
      alert('All tasks must have an S3 key');
      return;
    }
    dispatch('submit', { ...workflow });
    close();
  }
</script>

{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={close}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-4xl relative z-60 overflow-y-auto max-h-[90vh]" on:click|stopPropagation>
      <header class="flex justify-between items-start mb-4 text-gray-900 dark:text-gray-100">
        <h2 class="text-xl font-bold">{editWorkflow ? 'Edit Workflow' : 'Create Workflow'}</h2>
        <button 
          class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200" 
          on:click={close}
        >&times;</button>
      </header>

      <div class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div class="md:col-span-7">
            <label for="wf-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
            <input id="wf-name" class="border p-1 w-full h-8 rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100" bind:value={workflow.name} />
          </div>
          <div class="md:col-span-2">
            <label for="wf-version" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Version</label>
            <input id="wf-version" class="border p-1 w-full h-8 rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100" placeholder="e.g. 1.0.0" bind:value={workflow.version} />
          </div>
          <div class="flex items-center gap-2 whitespace-nowrap">
            <input id="wf-active" type="checkbox" bind:checked={workflow.active} />
            <label for="wf-active" class="text-sm text-gray-700 dark:text-gray-300">Active</label>
          </div>
          <div class="ml-4 flex items-center gap-2 whitespace-nowrap">
            <input id="wf-required" type="checkbox" bind:checked={workflow.required} />
            <label for="wf-required" class="text-sm text-gray-700 dark:text-gray-300">Required</label>
          </div>
        </div>
        <div>
          <label for="wf-desc" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
          <textarea id="wf-desc" rows="2" class="border p-2 w-full rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100" bind:value={workflow.description}></textarea>
        </div>

        <div class="border-t border-gray-200 dark:border-gray-700 pt-3">
          <div class="flex items-center justify-between mb-2">
            <h4 class="font-semibold text-gray-900 dark:text-gray-100">Tasks</h4>
            <button type="button" class="pagination mx-1 p-0" title="Add task" on:click={addTask}>
              <FontAwesomeIcon icon={faSquarePlus} class="text-sky-800 dark:text-white/70" />
            </button>
          </div>

          <div class="max-h-[50vh] overflow-y-auto pr-1">
            {#if (workflow.tasks || []).length === 0}
              <div class="text-sm text-gray-500 dark:text-gray-400">No tasks defined. Add your first task.</div>
            {:else}
              <div class="space-y-3">
                {#each workflow.tasks as task, index (task._id || index)}
                  <div class="p-3 rounded border dark:border-gray-700 bg-stone-50 dark:bg-gray-800">
                    <div class="flex items-center justify-between mb-2">
                      <div class="text-sm font-medium text-gray-800 dark:text-gray-200">Task {index + 1}</div>
                      <div class="flex items-center">
                        <button 
                          type="button" 
                          class="pagination mx-1 p-0"
                          title="Move up"
                          on:click={() => moveTaskUp(index)}
                          disabled={index === 0}
                          class:opacity-50={index === 0}
                          class:cursor-not-allowed={index === 0}
                        >
                          <FontAwesomeIcon icon={faChevronUp} class="text-gray-800 dark:text-white" />
                        </button>
                        <button 
                          type="button" 
                          class="pagination mx-1 p-0"
                          title="Move down"
                          on:click={() => moveTaskDown(index)}
                          disabled={index === (workflow.tasks.length - 1)}
                          class:opacity-50={index === (workflow.tasks.length - 1)}
                          class:cursor-not-allowed={index === (workflow.tasks.length - 1)}
                        >
                          <FontAwesomeIcon icon={faChevronDown} class="text-gray-800 dark:text-white" />
                        </button>
                        <button 
                          type="button" 
                          class="pagination mx-1 p-0"
                          title="Remove"
                          on:click={() => removeTask(index)}
                        >
                          <FontAwesomeIcon icon={faTrashCan} class="text-gray-800 dark:text-white" />
                        </button>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" for={`task-title-${index}`}>Title</label>
                        <input id={`task-title-${index}`} class="border p-1 w-full h-8 rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100" bind:value={task.title} />
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" for={`task-s3-${index}`}>S3 Key *</label>
                        <input id={`task-s3-${index}`} class="border p-1 w-full h-8 rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 font-mono" placeholder="protocol/site/participant/training/file.pdf" bind:value={task.s3Key} />
                      </div>
                      <div class="md:col-span-2">
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" for={`task-desc-${index}`}>Description</label>
                        <input id={`task-desc-${index}`} class="border p-1 w-full h-8 rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100" bind:value={task.description} />
                      </div>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </div>

        <div class="flex justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <button type="button" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700" on:click={close}>Cancel</button>
          <button type="button" class="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700" on:click={handleSubmit}>{editWorkflow ? 'Update' : 'Create'}</button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .z-60 { z-index: 60; }
</style>


